// KoachSignup: multi-step koach signup flow (account/personal/coach) with profile-image crop + upload.
import React, { useEffect, useRef, useState } from "react";

import * as ImageHelper from "helpers/Image.helper";
import * as DataApi from "api/common/Data.api";
import * as AuthApi from "api/common/Auth.api";

import PublicProfileImageCropModalComponent from "pages/student/v2/profile/PublicProfileImageCropModal.component";
import KoachSignupStep1 from "./KoachSignupStep1";
import KoachSignupStep2 from "./KoachSignupStep2";
import KoachSignupStep3 from "./KoachSignupStep3";
import KoachSignupDoneComponent from "./KoachSignupDone.component";
import Header from "../Header.component";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

const KoachSignup = (props: any) => {
    const { cancelSignup } = props;

    const [currentStep, setCurrentStep] = useState(1);
    const [isDone, setIsDone] = useState(false);
    const [isOnNetworking, setIsOnNetworking] = useState(false);
    const [stepValidations, setStepValidations] = useState({
        step1: false,
        step2: false,
        step3: false
    });

    const [accountParams, setAccountParams] = useState({
        username: "",
        password: "",
        confirmedPassword: "",
        nickname: "",
        handler: ""
    });

    const [personalParams, setPersonalParams] = useState({
        countryCode: "",
        firstName: "",
        lastName: "",
        genderTypeCode: "", // 기본값을 여성으로 설정
        countryPhoneCode: "",
        cellphone: "",
        verificationCode: "",
        isPhoneVerified: false
    });

    const [coachParams, setCoachParams] = useState({
        koachTypeCode: "",
        availableLanguageCodes: []
    });

    const [termList, setTermList] = useState<any>([]);

    useEffect(() => {
        getTermList();
    }, []);

    // 프로필 이미지 정보
    const [profileImageInfo, setProfileImageInfo] = useState<{
        id?: string;
        url?: string;
    } | null>(null);
    const [cropObjectUrl, setCropObjectUrl] = useState<string | null>(null);
    const pickedFileNameRef = useRef("");

    const getTermList = async () => {
        const result = await AuthApi.getTermListByType({ accountType: "koach" });

        if (result?.list) {
            result.list.map((item: any) => (item.checked = false));
            setTermList(result.list);
        }
    };

    const handleInputChange = (section: string, field: string, value: any) => {
        switch (section) {
            case 'account':
                setAccountParams(prev => ({ ...prev, [field]: value }));
                break;
            case 'personal':
                setPersonalParams(prev => ({ ...prev, [field]: value }));
                break;
            case 'coach':
                setCoachParams(prev => ({ ...prev, [field]: value }));
                break;
        }
    };

    const handleCheckboxChange = (section: string, idx: number, checked: boolean) => {
        setTermList((prev: any) => {
            const newList = [...prev];
            newList[idx].checked = checked;
            return newList;
        });
    };

    const closeCropModal = () => {
        if (cropObjectUrl) {
            URL.revokeObjectURL(cropObjectUrl);
        }
        setCropObjectUrl(null);
    };

    const onProfileImageFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const file = evt.target.files?.[0];
        evt.target.value = "";
        if (!file) return;

        if (!/^image\/(jpeg|jpg|png)$/i.test(file.type)) {
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "publicProfileEdit.txtCropInvalidFormat"));
            return;
        }

        pickedFileNameRef.current = file.name;
        setCropObjectUrl(URL.createObjectURL(file));
    };

    const onCropModalApply = async (blob: Blob) => {
        const rawName = pickedFileNameRef.current || "profile";
        const base = rawName.replace(/\.[^.]+$/i, "") || "profile";
        const file = new File([blob], `${base}.jpg`, { type: "image/jpeg" });
        closeCropModal();

        setIsOnNetworking(true);
        try {
            const preview = await ImageHelper.getImageInfo(file);
            if (!preview) {
                alert(getLanguageValue(PAGE_TYPE.COMMON, "validation.txtChooseImage"));
                return;
            }

            const result = await DataApi.uploadImage(file);
            setProfileImageInfo({ id: result.id, url: preview.url });
        } catch (err) {
            console.log("KoachSignup:onCropModalApply", err);
        } finally {
            setIsOnNetworking(false);
        }
    };

    // 각 스텝의 유효성 검사 상태를 업데이트
    const handleStepValidationChange = (step: number, isValid: boolean) => {
        setStepValidations(prev => ({
            ...prev,
            [`step${step}`]: isValid
        }));
    };

    // 현재 스텝이 유효한지 확인
    const isCurrentStepValid = () => {
        return stepValidations[`step${currentStep}` as keyof typeof stepValidations];
    };

    // 회원가입 완료 함수
    const handleSignupComplete = async () => {
        if (isOnNetworking) return;

        // 필수 약관 동의 검사
        if (!termList[0]?.checked) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "validation.txtAgreeWithTerms"));
            return;
        }
        if (!termList[1]?.checked) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "validation.txtAgreeWithPersonalPolicy"));
            return;
        }

        setIsOnNetworking(true);
        try {
            // API 요청 데이터 구성
            const signupData = {
                username: accountParams.username,
                password: accountParams.password,
                confirmedPassword: accountParams.confirmedPassword,
                nickname: accountParams.nickname,
                handler: accountParams.handler,
                countryCode: personalParams.countryCode,
                firstName: personalParams.firstName,
                lastName: personalParams.lastName,
                genderTypeCode: personalParams.genderTypeCode,
                countryPhoneCode: personalParams.countryPhoneCode,
                cellphone: personalParams.cellphone,
                verificationCode: personalParams.verificationCode,
                profileImageId: profileImageInfo?.id || "",
                koachTypeCode: coachParams.koachTypeCode,
                availableLanguageCodes: coachParams.availableLanguageCodes,
                // 약관 동의 값들 추가
                termsOfService: true, // 코치회원 이용약관 (항상 true)
                personalPolicy: true, // 개인정보 처리방침 (항상 true)
                receiveEmail: termList[2]?.checked || false, // 마케팅정보 수신 동의 - 이메일
                receiveMessage: termList[3]?.checked || false // 마케팅정보 수신 동의 - SMS/MMS/PUSH
            };

            // TODO: 실제 회원가입 API 호출
            const result = await AuthApi.signupKoach(signupData);
            console.log({ result });
            if (result.status === 200) {
                setIsDone(true);
            }
        } catch (error: any) {
            console.error("회원가입 실패:");
            console.log({ error });
        } finally {
            setIsOnNetworking(false);
        }
    };

    const nextStep = () => {
        if (currentStep < 3 && isCurrentStepValid()) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // currentStep이 변경될 때마다 스크롤을 상단으로 이동
    useEffect(() => {
        // 약간의 지연을 두어 DOM이 업데이트된 후 스크롤 실행
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    }, [currentStep]);

    const renderStepIndicator = () => (
        <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
                {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step <= currentStep
                            ? 'bg-[#CF3688] text-white'
                            : 'bg-gray-200 text-gray-500'
                            }`}>
                            {step}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <KoachSignupStep1
                        accountParams={accountParams}
                        handleInputChange={handleInputChange}
                        onValidationChange={(isValid) => handleStepValidationChange(1, isValid)}
                    />
                );
            case 2:
                return (
                    <KoachSignupStep2
                        personalParams={personalParams}
                        handleInputChange={handleInputChange}
                        onValidationChange={(isValid) => handleStepValidationChange(2, isValid)}
                        onProfileImageFileChange={onProfileImageFileChange}
                        profileImageInfo={profileImageInfo}
                    />
                );
            case 3:
                return (
                    <KoachSignupStep3
                        coachParams={coachParams}
                        handleInputChange={handleInputChange}
                        onValidationChange={(isValid) => handleStepValidationChange(3, isValid)}
                        termList={termList}
                        onChangeCheckbox={handleCheckboxChange}
                        isOnNetworking={isOnNetworking}
                    />
                );
            default:
                return (
                    <KoachSignupStep1
                        accountParams={accountParams}
                        handleInputChange={handleInputChange}
                        onValidationChange={(isValid) => handleStepValidationChange(1, isValid)}
                    />
                );
        }
    };

    // 회원가입 완료 화면 표시
    if (isDone) {
        return (
            <KoachSignupDoneComponent
                accountParams={accountParams}
                personalParams={personalParams}
                coachParams={coachParams}
                profileImageInfo={profileImageInfo}
            />
        );
    }

    return (
        <>
            <Header backUrl="/signup" />
            <main className="min-h-screen bg-gray-50 px-4 pb-12 pt-6 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <h2 className="md:text-4xl text-2xl font-bold text-center text-gray-900 mb-8 md:mb-12">
                        {getLanguageValue(PAGE_TYPE.KOACH, "signup.title")}
                    </h2>
                </div>

                {renderStepIndicator()}
                {renderCurrentStep()}

                <div className="flex justify-between space-x-4">
                    <button
                        type="button"
                        onClick={currentStep === 1 ? cancelSignup : prevStep}
                        className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                        {currentStep === 1 ? getLanguageValue(PAGE_TYPE.COMMON, "btn.txtCancel") : getLanguageValue(PAGE_TYPE.COMMON, "btn.txtPrev")}
                    </button>
                    <button
                        type="button"
                        onClick={currentStep === 3 ? handleSignupComplete : nextStep}
                        disabled={!isCurrentStepValid() || isOnNetworking}
                        className={`px-6 py-3 rounded-xl transition-colors ${isCurrentStepValid() && !isOnNetworking
                            ? 'text-white bg-[#CF3688] hover:bg-[#B52E74]'
                            : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                            }`}
                    >
                        {currentStep === 3 ? (isOnNetworking ? getLanguageValue(PAGE_TYPE.COMMON, "btn.txtProcessing") : getLanguageValue(PAGE_TYPE.COMMON, "btn.txtSignupComplete")) : getLanguageValue(PAGE_TYPE.COMMON, "btn.txtNext")}
                    </button>
                </div>
            </main>
            {cropObjectUrl && (
                <PublicProfileImageCropModalComponent
                    imageSrc={cropObjectUrl}
                    onClose={closeCropModal}
                    onApply={onCropModalApply}
                />
            )}
        </>
    );
};

export default KoachSignup;